import React, { useEffect, useState, useCallback, useRef } from 'react';
import api, { formatApiErrorDetail } from '../api';

/**
 * WithdrawSheet — Phase 3 crypto off-ramp modal.
 * Points are debited atomically before payout is triggered; if the provider
 * fails, the backend refunds Points automatically.
 */
export default function WithdrawSheet({ balance, onClose, onWithdrawn }) {
  const [currencies, setCurrencies] = useState([]);
  const [amount, setAmount] = useState('10');
  const [currency, setCurrency] = useState('');
  const [address, setAddress] = useState('');
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  // Saved addresses (per-currency). Auto-populated after every successful payout.
  const [savedAddrs, setSavedAddrs] = useState([]);
  // Currency dropdown open/close
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const currencyBtnRef = useRef(null);
  const currencyMenuRef = useRef(null);

  const loadSaved = useCallback((cur) => {
    if (!cur) return;
    api.get('/wallet/payouts/saved-addresses', { params: { currency: cur } })
      .then(({ data }) => setSavedAddrs(data.items || []))
      .catch(() => setSavedAddrs([]));
  }, []);

  useEffect(() => {
    api.get('/wallet/crypto/currencies')
      .then(({ data }) => setCurrencies(data.currencies || []))
      .catch(() => setCurrencies([
        { code: 'usdttrc20', name: 'USDT (TRC-20)' },
        { code: 'usdcerc20', name: 'USDC (ERC-20)' },
        { code: 'btc', name: 'Bitcoin' },
        { code: 'eth', name: 'Ethereum' },
      ]));
  }, []);

  useEffect(() => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0 || !currency) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post('/wallet/deposit/estimate', {
          usd_amount: n, pay_currency: currency,
        });
        setQuote(data);
      } catch { setQuote(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [amount, currency]);

  // Reload saved addresses whenever the picked currency changes (b1 — chips
  // are per-currency to avoid TRC20-vs-ERC20-vs-BTC address confusion).
  useEffect(() => {
    loadSaved(currency);
  }, [currency, loadSaved]);

  const removeSaved = async (id) => {
    try {
      await api.delete(`/wallet/payouts/saved-addresses/${id}`);
      setSavedAddrs((prev) => prev.filter((a) => a.id !== id));
    } catch { /* noop */ }
  };

  const shortAddr = (a) => (a && a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a);

  // Close currency dropdown on outside click or Escape
  useEffect(() => {
    if (!currencyOpen) return;
    const onDown = (e) => {
      if (currencyMenuRef.current?.contains(e.target)) return;
      if (currencyBtnRef.current?.contains(e.target)) return;
      setCurrencyOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setCurrencyOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [currencyOpen]);

  const currentCurrency = currencies.find((c) => c.code === currency);

  const usd = Number(amount);
  const inBounds = Number.isFinite(usd) && usd >= 10 && usd <= 2500;
  const enough = usd <= (balance || 0);
  const validAddress = address.trim().length >= 8;
  const canSubmit = inBounds && enough && validAddress && !!currency && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErr('');
    // Idempotency key — one per user tap. If the response is lost mid-flight
    // and the user retries, the backend returns the same withdrawal record
    // instead of debiting again. Same pattern as /trade/buy + /trade/sell.
    const clientOpId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `co-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      const { data } = await api.post('/wallet/withdraw', {
        usd_amount: usd, currency, address: address.trim(),
        client_op_id: clientOpId,
      });
      onWithdrawn(data.withdrawal);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not send withdrawal.');
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-scrim" onClick={onClose}>
      <div className="cs-sheet" onClick={(e) => e.stopPropagation()} data-testid="withdraw-sheet">
        <div className="cs-head">
          <div className="cs-title">Withdraw to crypto</div>
          <button className="cs-close" onClick={onClose} aria-label="Close" data-testid="withdraw-close-btn">×</button>
        </div>
        <p className="cs-sub">
          Cash out to any wallet address. Points debit is atomic — instant refund if payout fails.
        </p>

        <div className="cs-field">
          <label className="cs-label">Amount (USD)</label>
          <div className="cs-input-row">
            <span className="cs-prefix">$</span>
            <input
              type="number"
              min={10}
              max={2500}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="withdraw-amount"
            />
            <span className="cs-suffix">USD</span>
          </div>
          <div className="cs-hint">Min $10 · Max $2,500 · Balance {(balance || 0).toFixed(2)} pts</div>
        </div>

        <div className="cs-field">
          <label className="cs-label">Receive as</label>
          <div className="cs-currency-picker" data-testid="withdraw-currency-picker">
            <button
              ref={currencyBtnRef}
              type="button"
              className={`cs-currency-trigger${currencyOpen ? ' open' : ''}`}
              onClick={() => setCurrencyOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={currencyOpen}
              data-testid="withdraw-currency-trigger"
            >
              {currency ? (
                <>
                  <span className="cs-currency-trigger-main">
                    <span className="cs-cur-code">
                      {currency.toUpperCase().replace('TRC20', ' TRC').replace('ERC20', ' ERC').replace('BSC', ' BEP-20')}
                    </span>
                    <span className="cs-cur-name">{currentCurrency?.name || ''}</span>
                  </span>
                  {currentCurrency?.gas_note && (
                    <span className={`cs-gas-tag cs-gas-${currentCurrency.gas_tone || 'neutral'}`}>
                      {currentCurrency.gas_note}
                    </span>
                  )}
                </>
              ) : (
                <span className="cs-currency-trigger-main">
                  <span className="cs-cur-code cs-cur-placeholder">Choose network</span>
                  <span className="cs-cur-name">Select a coin & chain to receive</span>
                </span>
              )}
              <span className="cs-currency-caret" aria-hidden="true">▾</span>
            </button>

            {currencyOpen && (
              <div
                ref={currencyMenuRef}
                className="cs-currency-menu"
                role="listbox"
                data-testid="withdraw-currency-menu"
              >
                {currencies.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    role="option"
                    aria-selected={currency === c.code}
                    className={`cs-currency-item${currency === c.code ? ' selected' : ''}`}
                    onClick={() => { setCurrency(c.code); setCurrencyOpen(false); }}
                    data-testid={`withdraw-currency-${c.code}`}
                  >
                    <span className="cs-currency-item-main">
                      <span className="cs-cur-code">
                        {c.code.toUpperCase().replace('TRC20', ' TRC').replace('ERC20', ' ERC').replace('BSC', ' BEP-20')}
                      </span>
                      <span className="cs-cur-name">{c.name}</span>
                    </span>
                    {c.gas_note && (
                      <span className={`cs-gas-tag cs-gas-${c.gas_tone || 'neutral'}`}>
                        {c.gas_note}
                      </span>
                    )}
                    {currency === c.code && <span className="cs-currency-check">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="cs-field">
          <label className="cs-label">Wallet address</label>

          {savedAddrs.length > 0 && (
            <div className="cs-saved-addr-row" data-testid="withdraw-saved-addresses">
              <div className="cs-saved-addr-label">Recent addresses</div>
              <div className="cs-saved-addr-chips">
                {savedAddrs.map((a) => (
                  <div
                    key={a.id}
                    className={`cs-saved-chip${address.trim() === a.address ? ' selected' : ''}`}
                    onClick={() => setAddress(a.address)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAddress(a.address); }}
                    data-testid={`withdraw-saved-chip-${a.id}`}
                  >
                    <span className="cs-saved-chip-addr">{shortAddr(a.address)}</span>
                    <span className="cs-saved-chip-meta">
                      {a.uses_count > 1 ? `used ${a.uses_count}×` : 'used once'}
                    </span>
                    <button
                      type="button"
                      className="cs-saved-chip-x"
                      onClick={(e) => { e.stopPropagation(); removeSaved(a.id); }}
                      aria-label={`Remove ${shortAddr(a.address)}`}
                      data-testid={`withdraw-saved-remove-${a.id}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <input
            type="text"
            className="cs-address-input"
            placeholder="Paste your destination wallet address"
            value={address}
            onChange={(e) => setAddress(e.target.value.slice(0, 200))}
            data-testid="withdraw-address"
          />
          <div className="cs-hint" style={{ color: 'var(--error)' }}>
            Double-check the address & network — payouts are irreversible.
          </div>
        </div>

        <div className="cs-quote" data-testid="withdraw-quote">
          <div className="cs-quote-row"><span>Debit from balance</span><b>{inBounds ? usd.toFixed(2) : '—'} pts</b></div>
          <div className="cs-quote-row"><span>You receive</span><b>{quote && currency ? `${quote.estimated_amount} ${currency.toUpperCase()}` : '—'}</b></div>
        </div>

        {err && <div className="cs-err" data-testid="withdraw-err">{err}</div>}
        {!enough && inBounds && <div className="cs-err">Not enough Points — you have {(balance || 0).toFixed(2)}.</div>}

        <button
          className="cs-btn-primary"
          disabled={!canSubmit}
          onClick={submit}
          data-testid="withdraw-submit"
        >
          {submitting ? 'Sending payout…' : 'Send withdrawal'}
        </button>
      </div>
    </div>
  );
}
